import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { authDarkFieldSx, authPrimaryButtonSx, authLinkButtonSx } from './authStyles';

const ResetPassword = ({ email, onBackToLogin, onPasswordReset }) => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, code.trim(), newPassword);
      setSuccess(true);
      setTimeout(() => {
        onPasswordReset();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
        Code sent to <strong style={{ color: 'rgba(255,255,255,0.95)' }}>{email}</strong>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>
          Password updated. Redirecting to sign in…
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Verification code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          margin="normal"
          required
          autoComplete="one-time-code"
          autoFocus
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          placeholder="6-digit code"
          disabled={success}
          sx={authDarkFieldSx}
        />
        <TextField
          fullWidth
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          required
          autoComplete="new-password"
          helperText="At least 8 characters"
          disabled={success}
          sx={authDarkFieldSx}
        />
        <TextField
          fullWidth
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          required
          autoComplete="new-password"
          disabled={success}
          sx={authDarkFieldSx}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || success}
          sx={authPrimaryButtonSx}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset password'}
        </Button>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Remember your password?{' '}
          <Link component="button" type="button" variant="body2" onClick={onBackToLogin} sx={authLinkButtonSx}>
            Back to login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default ResetPassword;
